// src/components/ProjectToolbar.js

import { useState, useEffect, useCallback } from "react"; // Add React hooks
import { Circle } from "fabric"; // Add Fabric.js classes
import {
  Box,
  Button,
  Stack,
  Popper,
  ClickAwayListener,
  Tooltip,
  Toolbar,
  Divider,
} from "@mui/material"; // Add MUI components
import FormatAlignCenter from "@mui/icons-material/FormatAlignCenter"; // Correct AlignIcon
import LayersIcon from "@mui/icons-material/Layers";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { useData } from "../hooks/useData"; // Add custom hook
import useCanvasHistory from "../hooks/useCanvasHistory"; // Add custom hook
import AlignToolbar from "./AlignToolbar"; // Add custom component
import DraggableColorPicker from "./DraggableColorPicker"; // Add custom component
import DraggableLayersPopover from "./DraggableLayersPopover"; // Add custom component
import DownloadButton from "./DownloadButton"; // Add custom component
import ProjectName from "./ProjectName"; // Add custom component
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DesignHelpPopover from "./DesignHelpPopover";
import SharePopover from "./SharePopover";

const ProjectToolbar = ({
  showColorPicker: externalShowColorPicker,
  setShowColorPicker,
}) => {
  const { canvas, selectedObject, setSelectedObject, setSnackbar } = useData();
  const { saveHistory } = useCanvasHistory(canvas);
  const [internalShowColorPicker, setInternalShowColorPicker] = useState(false);
  const [showLayersPopover, setShowLayersPopover] = useState(false);
  const [isCopyDisabled, setIsCopyDisabled] = useState(true);
  const [isAlignDisabled, setIsAlignDisabled] = useState(true);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [isLockDisabled, setIsLockDisabled] = useState(true);
  const [isColorPickerDisabled, setIsColorPickerDisabled] = useState(true);
  const [isLayersDisabled, setIsLayersDisabled] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [showDesignHelpPopover, setShowDesignHelpPopover] = useState(false);
  const [showSharePopover, setShowSharePopover] = useState(false);
  const [isDesignHelpDisabled, setIsDesignHelpDisabled] = useState(true);
  const [isShareDisabled, setIsShareDisabled] = useState(true);

  const showColorPicker =
    typeof externalShowColorPicker !== "undefined"
      ? externalShowColorPicker
      : internalShowColorPicker;
  const setShowColorPickerEffective =
    typeof setShowColorPicker === "function"
      ? setShowColorPicker
      : setInternalShowColorPicker;

  console.log("ProjectToolbar: Props received", {
    showColorPicker,
    setShowColorPicker,
  });

  useEffect(() => {
    if (!canvas) {
      console.warn("ProjectToolbar: Canvas not initialized");
      setSnackbar({
        open: true,
        message: "Canvas not initialized. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    if (!canvas.selection) {
      console.error("ProjectToolbar: Canvas selection is disabled");
      setSnackbar({
        open: true,
        message:
          "Canvas selection is disabled. Multiple object selection unavailable.",
        severity: "error",
      });
    }

    console.log("ProjectToolbar: Canvas selection settings", {
      selection: canvas.selection,
      selectable: canvas.getObjects().every((obj) => obj.selectable),
    });

    const handleSelection = (event) => {
      const activeObj = canvas.getActiveObject();
      const activeObjects = canvas.getActiveObjects();
      // Selected object is an ActiveSelection
      const isActiveSelection =
        selectedObject && selectedObject.get("type") === "activeselection";

      console.log("ProjectToolbar: Selection event", {
        eventType: event?.type,
        selected: event?.selected?.length || 0,
        activeObjects: activeObjects.length,
        activeObjType: activeObj?.type,
        isActiveSelection,
      });

      if (!activeObj || activeObjects.length === 0) {
        setIsCopyDisabled(true);
        setIsAlignDisabled(true);
        setIsDeleteDisabled(true);
        setIsLockDisabled(true);
        setIsColorPickerDisabled(true);
        setSelectedObject(null);
        setIsLocked(false);
        console.log("ProjectToolbar: No active object");
        return;
      }

      const objectCount = isActiveSelection ? activeObjects.length : 1;

      console.log("[ProjectToolbar] Active object", {
        type: activeObj.type,
        objectCount,
        objects: activeObjects.map((obj) => ({ type: obj.type, id: obj.id })),
      });

      setIsCopyDisabled(isActiveSelection);
      setIsAlignDisabled(!(isActiveSelection && objectCount >= 2));
      setIsDeleteDisabled(false);
      setIsLockDisabled(false);
      setIsColorPickerDisabled(false);
      setIsLocked(activeObj.lockMovementX && activeObj.lockMovementY);
      setSelectedObject(activeObj);
    };

    const handleSelectionCleared = () => {
      console.log("ProjectToolbar: Selection cleared");
      setIsCopyDisabled(true);
      setIsAlignDisabled(true);
      setIsDeleteDisabled(true);
      setIsLockDisabled(true);
      setIsColorPickerDisabled(true);
      setSelectedObject(null);
      setIsLocked(false);
      setShowColorPickerEffective(false);
      setShowLayersPopover(false);
    };

    const updateLayersDisabled = () => {
      const objects = canvas.getObjects();
      setIsLayersDisabled(objects.length < 2);
      setIsDesignHelpDisabled(objects.length === 0);
      setIsShareDisabled(objects.length === 0);
      console.log("ProjectToolbar: Layers disabled check", {
        objectCount: objects.length,
      });
    };

    canvas.on("selection:created", (e) => {
      console.log("ProjectToolbar: selection:created fired", {
        selected: e.selected,
      });
      handleSelection(e);
    });
    canvas.on("selection:updated", (e) => {
      console.log("ProjectToolbar: selection:updated fired", {
        selected: e.selected,
      });
      handleSelection(e);
    });
    canvas.on("selection:cleared", handleSelectionCleared);
    canvas.on("object:added", updateLayersDisabled);
    canvas.on("object:removed", updateLayersDisabled);

    updateLayersDisabled();
    handleSelection({ selected: canvas.getActiveObjects() });

    return () => {
      canvas.off("selection:created");
      canvas.off("selection:updated");
      canvas.off("selection:cleared");
      canvas.off("object:added");
      canvas.off("object:removed");
    };
  }, [
    canvas,
    selectedObject,
    setSelectedObject,
    setSnackbar,
    setShowColorPickerEffective,
  ]); // Added setShowColorPickerEffective

  const toggleColorPicker = () => {
    setShowColorPickerEffective((prev) => !prev);
  };

  const toggleLayersPopover = () => {
    setShowLayersPopover((prev) => !prev);
  };

  const handleCopyObject = useCallback(async () => {
    if (!canvas || !canvas.__isMounted) {
      console.error("ProjectToolbar: Canvas is not available or not mounted");
      setSnackbar({
        open: true,
        message: "Canvas not available. Please try again.",
        severity: "error",
      });
      return;
    }

    const activeObj = canvas.getActiveObject();
    if (!activeObj) {
      console.error("ProjectToolbar: No active object selected for copying");
      setIsCopyDisabled(true);
      setSnackbar({
        open: true,
        message: "No object selected to copy.",
        severity: "warning",
      });
      return;
    }

    if (activeObj.type === "activeselection") {
      console.error("ProjectToolbar: Copying activeselection is not supported");
      setIsCopyDisabled(true);
      setSnackbar({
        open: true,
        message: "Copying multiple objects is not supported.",
        severity: "warning",
      });
      return;
    }

    console.log("ProjectToolbar: Copying object", {
      type: activeObj.type,
      left: activeObj.left,
      top: activeObj.top,
      properties: Object.keys(activeObj).filter((key) => !key.startsWith("_")),
    });

    try {
      let clonedObj;
      if (activeObj.type === "circle") {
        clonedObj = new Circle({
          ...activeObj.toObject([
            "id",
            "name",
            "fill",
            "stroke",
            "strokeWidth",
            "strokeDashArray",
            "strokeLineCap",
            "strokeLineJoin",
            "strokeMiterLimit",
            "left",
            "top",
            "scaleX",
            "scaleY",
            "angle",
            "opacity",
            "radius",
            "startAngle",
            "endAngle",
            "counterClockwise",
            "shadow",
            "visible",
            "backgroundColor",
            "fillRule",
            "paintFirst",
            "globalCompositeOperation",
            "skewX",
            "skewY",
            "originX",
            "originY",
          ]),
        });
      } else {
        clonedObj = await new Promise((resolve, reject) => {
          activeObj.clone(
            (cloned) => {
              if (!cloned) {
                reject(new Error("Cloning failed, cloned object is undefined"));
              } else {
                resolve(cloned);
              }
            },
            [
              "id",
              "name",
              "fill",
              "stroke",
              "strokeWidth",
              "strokeDashArray",
              "strokeLineCap",
              "strokeLineJoin",
              "strokeMiterLimit",
              "left",
              "top",
              "scaleX",
              "scaleY",
              "angle",
              "opacity",
              "shadow",
              "visible",
              "backgroundColor",
              "fillRule",
              "paintFirst",
              "globalCompositeOperation",
              "skewX",
              "skewY",
              "originX",
              "originY",
            ]
          );
        });
      }

      canvas.discardActiveObject();
      const zoom = canvas.getZoom() || 1;
      const offset = 10 / zoom;
      const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
      const newLeft = (Number(activeObj.left || 0) + offset - vpt[4]) / zoom;
      const newTop = (Number(activeObj.top || 0) + offset - vpt[5]) / zoom;

      clonedObj.set({
        left: newLeft,
        top: newTop,
        evented: true,
      });

      canvas.add(clonedObj);
      canvas.setActiveObject(clonedObj);
      canvas.requestRenderAll();
      saveHistory(canvas);
      setSnackbar({
        open: true,
        message: "Object copied successfully.",
        severity: "success",
      });

      console.log("ProjectToolbar: Cloned object added", {
        left: newLeft,
        top: newTop,
        type: clonedObj.type,
      });
    } catch (error) {
      console.error("ProjectToolbar: Error copying object", {
        error: error.message,
        stack: error.stack,
      });
      setSnackbar({
        open: true,
        message: `Failed to copy object: ${error.message}`,
        severity: "error",
      });
    }
  }, [canvas, saveHistory, setSnackbar]);

  const handleAlignClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAlignClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteObject = useCallback(() => {
    if (!canvas || !canvas.__isMounted) {
      console.error("ProjectToolbar: Canvas is not available or not mounted");
      setSnackbar({
        open: true,
        message: "Canvas not available. Please try again.",
        severity: "error",
      });
      return;
    }

    const activeObj = canvas.getActiveObject();
    if (!activeObj) {
      console.error("ProjectToolbar: No active object selected for deletion");
      setIsDeleteDisabled(true);
      setSnackbar({
        open: true,
        message: "No object selected to delete.",
        severity: "warning",
      });
      return;
    }

    console.log("ProjectToolbar: Deleting object", {
      type: activeObj.type,
      left: activeObj.left,
      top: activeObj.top,
    });

    canvas.remove(activeObj);
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    saveHistory(canvas);
    setSnackbar({
      open: true,
      message: "Object deleted successfully.",
      severity: "success",
    });
  }, [canvas, saveHistory, setSnackbar]);

  const handleLockObject = useCallback(() => {
    if (!canvas || !canvas.__isMounted) {
      console.error("ProjectToolbar: Canvas is not available or not mounted");
      setSnackbar({
        open: true,
        message: "Canvas not available. Please try again.",
        severity: "error",
      });
      return;
    }

    const activeObj = canvas.getActiveObject();
    if (!activeObj) {
      console.error("ProjectToolbar: No active object selected for locking");
      setIsLockDisabled(true);
      setSnackbar({
        open: true,
        message: "No object selected to lock/unlock.",
        severity: "warning",
      });
      return;
    }

    const newLockState = !isLocked;
    activeObj.set({
      lockMovementX: newLockState,
      lockMovementY: newLockState,
      hasControls: !newLockState,
      selectable: !newLockState,
    });

    console.log("ProjectToolbar: Toggling lock state", {
      type: activeObj.type,
      lockMovementX: newLockState,
      lockMovementY: newLockState,
    });

    setIsLocked(newLockState);
    activeObj.setCoords();
    canvas.requestRenderAll();
    saveHistory(canvas);
    setSnackbar({
      open: true,
      message: newLockState ? "Object locked." : "Object unlocked.",
      severity: "info",
    });
  }, [canvas, isLocked, saveHistory, setSnackbar]);

  const open = Boolean(anchorEl);

  const toggleDesignHelpPopover = () => {
    setShowDesignHelpPopover((prev) => !prev);
  };

  const toggleSharePopover = () => {
    setShowSharePopover((prev) => !prev);
  };

  return (
    <Toolbar
      sx={{
        minHeight: { xs: 56, sm: 64 },
        backgroundColor: "white",
        justifyContent: "space-between",
        px: 2,
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", ml: 4 }}>
        <ProjectName />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mr: 4 }}>
        <Tooltip
          title="Color Picker"
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={
              <PaletteOutlinedIcon
                sx={{
                  color: isColorPickerDisabled
                    ? "text.secondary"
                    : "primary.main",
                }}
              />
            }
            disabled={isColorPickerDisabled}
            onClick={toggleColorPicker}
            sx={{ textTransform: "none" }}
          >
            Color
          </Button>
        </Tooltip>
        {showColorPicker && (
          <DraggableColorPicker
            canvas={canvas}
            onClose={() => setShowColorPickerEffective(false)}
          />
        )}
        <ClickAwayListener onClickAway={handleAlignClose}>
          <Box>
            <Tooltip
              title="Aligns selected objects amongst each other, not the artboard."
              enterDelay={600}
              leaveDelay={200}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#454545",
                    color: "#fff",
                    maxWidth: "140px",
                    height: "40px",
                    fontSize: "12px",
                    textAlign: "center",
                    "& .MuiTooltip-arrow::before": {
                      backgroundColor: "#454545",
                    },
                  },
                },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<FormatAlignCenter />}
                disabled={isAlignDisabled}
                onClick={handleAlignClick}
                sx={{ textTransform: "none" }}
              >
                Align
              </Button>
            </Tooltip>
            <Popper
              open={open}
              anchorEl={anchorEl}
              placement="bottom"
              sx={{
                zIndex: 1300,
                marginTop: "8px !important",
                "&.MuiPopper-root": {
                  backgroundColor: "white",
                  boxShadow: 3,
                  borderRadius: "4px",
                  "&::before": {
                    backgroundColor: "white",
                    content: '""',
                    display: "block",
                    position: "absolute",
                    width: 12,
                    height: 12,
                    top: -6,
                    transform: "rotate(45deg)",
                    left: "calc(50% - 6px)",
                    boxShadow: "-2px -2px 4px rgba(0, 0, 0, 0.1)",
                  },
                },
              }}
            >
              <AlignToolbar canvas={canvas} />
            </Popper>
          </Box>
        </ClickAwayListener>
        <Tooltip
          title="Manage layers by moving objects above or below each other."
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                maxWidth: "140px",
                height: "40px",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<LayersIcon />}
            disabled={isLayersDisabled}
            onClick={toggleLayersPopover}
            sx={{ textTransform: "none" }}
          >
            Layers
          </Button>
        </Tooltip>
        {showLayersPopover && (
          <DraggableLayersPopover
            canvas={canvas}
            saveHistory={saveHistory}
            onClose={() => setShowLayersPopover(false)}
          />
        )}
        <Tooltip
          title="Copy the selected object"
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            disabled={isCopyDisabled}
            onClick={handleCopyObject}
            sx={{ textTransform: "none" }}
          >
            Copy
          </Button>
        </Tooltip>
        <Tooltip
          title="Delete the selected object"
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            disabled={isDeleteDisabled}
            onClick={handleDeleteObject}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </Tooltip>
        <Tooltip
          title={
            isLocked ? "Unlock the selected object" : "Lock the selected object"
          }
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={isLocked ? <LockIcon /> : <LockOpenIcon />}
            disabled={isLockDisabled}
            onClick={handleLockObject}
            sx={{ textTransform: "none" }}
          >
            {isLocked ? "Unlock" : "Lock"}
          </Button>
        </Tooltip>
        <Box sx={{ mr: 2 }}>
          <DownloadButton canvas={canvas} />
        </Box>
        <Divider
          orientation="vertical"
          variant="middle"
          sx={{ ml: 4, mr: 4 }}
          flexItem
        />
        <Tooltip
          title="Request professional design help from Alphabet Signs"
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={
              <AutoAwesomeIcon
                sx={{
                  color: isDesignHelpDisabled ? "text.secondary" : "blue",
                }}
              />
            }
            disabled={isDesignHelpDisabled}
            onClick={toggleDesignHelpPopover}
            sx={{ textTransform: "none" }}
          >
            Design Help
          </Button>
        </Tooltip>
        {showDesignHelpPopover && (
          <DesignHelpPopover
            canvas={canvas}
            open={showDesignHelpPopover}
            onClose={() => setShowDesignHelpPopover(false)}
          />
        )}
        <Tooltip
          title="Share your design with a friend via email"
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={
              <GroupsOutlinedIcon
                sx={{
                  color: isShareDisabled ? "text.secondary" : "primary.main",
                }}
              />
            }
            disabled={isShareDisabled}
            onClick={toggleSharePopover}
            sx={{ textTransform: "none" }}
          >
            Share
          </Button>
        </Tooltip>
        {showSharePopover && (
          <SharePopover
            canvas={canvas}
            open={showSharePopover}
            onClose={() => setShowSharePopover(false)}
          />
        )}
      </Stack>
    </Toolbar>
  );
};

export default ProjectToolbar;
